/**
 * What a record's own location section shows, for a given set of FORM VALUES.
 *
 * Pure, and in its own file, because `apps/console` runs its unit tests with no
 * jsdom (`vitest.config.ts` — forms are rendered with `react-dom/server` and
 * cannot be typed into). So the half that can be proven here is the DECISION:
 * which state a set of values produces, and what it says. The half that needs a
 * keystroke — that typing a latitude moves the pin and that Cancel puts it back —
 * is proven in the browser matrix, against the real form.
 *
 * Stating that split rather than writing a jsdom-shaped test that cannot run is
 * the point: a suite that pretended to drive the form would assert against a
 * string of server-rendered HTML and pass whatever the interaction did.
 */
import { asCoordinate, isPlaceable, type MapPin, type MapUnplaceableRecord } from '@solum/ui'
import type { MapRecipe } from '@solum/experience'

export interface RecordLocationInput {
  entity: string
  /** '' while creating — a record that does not exist yet has no destination. */
  id: string
  recipe: MapRecipe
  values: Record<string, unknown>
  title: string
  subtitle?: string
  isNew?: boolean
}

export interface RecordLocationView {
  pins: MapPin[]
  unplaceable: MapUnplaceableRecord[]
}

export function recordLocationView(input: RecordLocationInput): RecordLocationView {
  const { entity, id, recipe, values, title, subtitle, isNew = false } = input
  const rawLat = values[recipe.lat]
  const rawLng = values[recipe.lng]
  const shared = {
    key: `${entity}:${id || 'new'}`,
    entity,
    id,
    title,
    ...(subtitle ? { subtitle } : {}),
    // A record you are looking at is not a destination, and a record being
    // created has none at all. An affordance a host should not offer is a prop
    // it does not pass — never a no-op handler, which announces a command that
    // does nothing.
    openable: false,
  }

  // The SAME placement decision the lens and the relationship workspace use.
  // Three copies of this rule is how a pin and a list come to disagree about
  // which records exist.
  if (!isPlaceable(rawLat, rawLng)) {
    return { pins: [], unplaceable: [{ ...shared, reason: reasonFor(rawLat, rawLng, isNew) }] }
  }
  return {
    pins: [{ ...shared, lat: asCoordinate(rawLat)!, lng: asCoordinate(rawLng)! }],
    unplaceable: [],
  }
}

/**
 * Why there is no pin — and a CREATE says something different from a saved
 * record with no coordinates.
 *
 * "No coordinates yet" on a form somebody is still filling in reads as a
 * complaint about work they have not done; "they will appear here" is the same
 * fact as an invitation. One branch, and it is the difference between a form
 * that nags and one that explains.
 */
export function reasonFor(lat: unknown, lng: unknown, isNew: boolean): string {
  const missing = (v: unknown) =>
    v === null || v === undefined || (typeof v === 'string' && v.trim() === '')
  if (missing(lat) && missing(lng)) {
    return isNew
      ? 'Enter a latitude and longitude and the location will appear here.'
      : 'This record has no coordinates yet, so it cannot be placed on the map.'
  }
  if (missing(lat) || missing(lng)) return 'Only one coordinate is set, so there is nothing to place yet.'
  return 'The coordinates entered are not a valid latitude and longitude.'
}
